import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core'
import { MatIconButton } from '@angular/material/button'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import {
  ElderBasicPaneLayoutComponent,
  ElderFormFieldLabelDirective,
  ElderPaneActionsComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneSubtitleComponent,
  ElderQuantityPipe,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { WorkItem, WorkItemSegment } from '../../../../orders/work-items/work-item'
import { OutputPreviewService } from '../../../../output/preview/output-preview.service'
import { PrintButtonComponent } from '../../../../output/print/print-button/print-button.component'
import { PrintCommandDirective } from '../../../../output/print/print-command.directive'
import { PrintOptions, ReportSpec } from '../../../../output/report/report-job'
import { PropertyDisplayComponent } from '../../../../properties/property-display/property-display.component'
import { ControlTerminal } from '../../../control-terminal'
import { TerminalWorkItemInstructionsComponent } from '../terminal-work-item-instructions/terminal-work-item-instructions.component'

@Component({
  selector: 'app-terminal-work-item-detail',
  templateUrl: './terminal-work-item-detail.component.html',
  styleUrls: ['./terminal-work-item-detail.component.scss'],
  imports: [
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneContentComponent,
    ElderPaneHeaderComponent,
    ElderPaneActionsComponent,
    ElderPaneSubtitleComponent,
    PrintButtonComponent,
    PrintCommandDirective,
    MatIconButton,
    MatMenuTrigger,
    MatIcon,
    MatFormField,
    ElderFormFieldLabelDirective,
    MatLabel,
    MatInput,
    PropertyDisplayComponent,
    TerminalWorkItemInstructionsComponent,
    MatMenu,
    MatMenuItem,
    AsyncPipe,
    ElderTranslatePipe,
    ElderQuantityPipe,
  ],
})
export class TerminalWorkItemDetailComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly workItem$ = new BehaviorSubject<WorkItem>(null)
  public readonly controlTerminal$ = new BehaviorSubject<ControlTerminal>(null)

  public readonly labelReport$: Observable<ReportSpec>
  public readonly labelPrintOptions$: Observable<PrintOptions>

  public readonly printingEnabled$ = new BehaviorSubject<boolean>(false)

  public readonly workCenterId$ = new BehaviorSubject<string>(null)

  private readonly _currentSegment$ = new BehaviorSubject<WorkItemSegment>(null)

  private outputPreviewService = inject(OutputPreviewService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.labelReport$ = this.workItem$.pipe(map((item) => ReportSpec.itemLabel(item.id)))

    this.labelPrintOptions$ = this.controlTerminal$.pipe(
      filter((terminal) => !!terminal.labelPrinter),
      map((terminal) => PrintOptions.printer(terminal.labelPrinter.printerId))
    )
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set workItem(item: WorkItem) {
    if (item) {
      this.workItem$.next(item)
    }
  }

  public get workItem(): WorkItem {
    return this.workItem$.getValue()
  }

  @Input()
  public set workCenterId(workCenterId: string) {
    if (workCenterId) {
      this.workCenterId$.next(workCenterId)
      const currentSegment = this.determineCurrentSegment(workCenterId)
      this._currentSegment$.next(currentSegment)
    }
  }

  public get workCenterId(): string {
    return this.workCenterId$.getValue()
  }

  @Input()
  public set controlTerminal(controlTerminal: ControlTerminal) {
    this.log.debug(`Setting controlTerminal ${controlTerminal?.controlCenterId}`)
    this.controlTerminal$.next(controlTerminal)
  }

  public get controlTerminal(): ControlTerminal {
    return this.controlTerminal$.getValue()
  }

  @Input()
  public set printingEnabled(printingEnabled: boolean) {
    this.printingEnabled$.next(printingEnabled)
  }

  public get currentSegment$(): Observable<WorkItemSegment> {
    return this._currentSegment$.asObservable()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onPreviewItemLabel(item: WorkItem): void {
    this.outputPreviewService.openPreviewDialog(ReportSpec.itemLabel(item.id))
  }

  public onPreviewPieceLabel(item: WorkItem): void {
    this.outputPreviewService.openPreviewDialog(ReportSpec.itemPieceLabel(item.id))
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private determineCurrentSegment(workCenterId: string): WorkItemSegment | null {
    if (this.workItem && workCenterId) {
      return this.workItem?.productionRoute.sequence.find(
        (sequence) => sequence.workCenterId === workCenterId
      )
    } else {
      return null
    }
  }
}
